import { useMemo } from "react";
import * as THREE from "three";
import { Box, Cyl, Ball } from "./Box";
import { getArt } from "./art";

const Mat = ({ c, r = 0.7, m = 0, e }) => <meshStandardMaterial color={c} roughness={r} metalness={m} emissive={e || "#000"} />;

export const ArtFrame = ({ position, rotation, w, h, art, frame = "#2A211B", depth = 0.06, mat = true }) => {
  const inset = mat ? 0.14 : 0.06;
  return (
    <group position={position} rotation={rotation}>
      <Box p={[0, 0, depth / 2]} s={[w, h, depth]} c={frame} r={0.45} m={frame === "#B8862E" ? 0.5 : 0} t="wood" round={0.008} />
      {mat && <Box p={[0, 0, depth + 0.004]} s={[w - 0.06, h - 0.06, 0.008]} c="#F4EEE2" r={0.9} t="paper" shadow={false} />}
      <mesh position={[0, 0, depth + 0.011]}>
        <planeGeometry args={[w - inset, h - inset]} />
        <meshStandardMaterial map={getArt(art)} roughness={mat ? 0.55 : 0.8} bumpMap={mat ? null : getArt(art)} bumpScale={1} />
      </mesh>
      {mat && (
        <mesh position={[0, 0, depth + 0.016]}>
          <planeGeometry args={[w - 0.06, h - 0.06]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.06} roughness={0.05} metalness={0.2} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
};

const Eyes = ({ y, z, gap, r }) => [-1, 1].map((s) => (
  <group key={s} position={[s * gap, y, z]}>
    <Ball rad={r} c="#F7F4EE" r={0.3} seg={12} />
    <Ball p={[0, 0, r * 0.7]} rad={r * 0.45} c="#111" r={0.2} seg={10} />
  </group>
));

// Mon voisin Totoro — Miyazaki
export const Totoro = ({ position, rotation, scale = 1 }) => (
  <group position={position} rotation={rotation} scale={scale}>
    <Ball p={[0, 0.14, 0]} rad={0.12} c="#7E8088" r={0.95} sc={[1, 1.18, 0.92]} t="rug" />
    <Ball p={[0, 0.12, 0.06]} rad={0.09} c="#E9E1CF" r={0.95} sc={[1, 1.1, 0.6]} t="rug" />
    {[[-0.03, 0.17], [0.03, 0.17], [0, 0.13]].map(([x, y], i) => (
      <Box key={i} p={[x, y, 0.115]} s={[0.025, 0.008, 0.01]} c="#6E7078" shadow={false} />
    ))}
    {[-1, 1].map((s) => (
      <mesh key={s} position={[s * 0.045, 0.3, 0]} rotation={[0, 0, -s * 0.12]} castShadow>
        <coneGeometry args={[0.022, 0.08, 10]} />
        <Mat c="#7E8088" r={0.95} />
      </mesh>
    ))}
    <Eyes y={0.235} z={0.085} gap={0.04} r={0.017} />
    <Ball p={[0, 0.225, 0.105]} rad={0.008} c="#222" seg={8} />
    <Ball p={[-0.05, 0.02, 0.04]} rad={0.03} c="#6E7078" sc={[1, 0.5, 1.4]} />
    <Ball p={[0.05, 0.02, 0.04]} rad={0.03} c="#6E7078" sc={[1, 0.5, 1.4]} />
    <mesh position={[0, 0.33, 0]} rotation={[0.2, 0, 0.5]} scale={[1, 0.25, 0.6]} castShadow>
      <sphereGeometry args={[0.03, 10, 8]} />
      <Mat c="#6E9A4A" />
    </mesh>
  </group>
);

// Noiraudes — Le Voyage de Chihiro
export const SootSprites = ({ position }) => (
  <group position={position}>
    {[[0, 0.03, 0], [0.07, 0.025, 0.03], [-0.06, 0.028, 0.04]].map((p, i) => (
      <group key={i} position={p}>
        <mesh castShadow>
          <icosahedronGeometry args={[0.028, 1]} />
          <meshStandardMaterial color="#141414" roughness={1} flatShading />
        </mesh>
        <Eyes y={0.006} z={0.022} gap={0.01} r={0.007} />
      </group>
    ))}
  </group>
);

// Kodama — Princesse Mononoké
export const Kodama = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <mesh position={[0, 0.045, 0]} castShadow>
      <capsuleGeometry args={[0.022, 0.05, 4, 10]} />
      <Mat c="#E9EEE4" r={0.4} />
    </mesh>
    <Ball p={[0, 0.115, 0]} rad={0.04} c="#E9EEE4" r={0.4} sc={[1.15, 0.9, 1]} />
    {[[-0.015, 0.12], [0.017, 0.118], [0, 0.098]].map(([x, y], i) => (
      <Ball key={i} p={[x, y, 0.035]} rad={0.007} c="#1A1A1A" seg={8} />
    ))}
  </group>
);

// Chapeau de paille — One Piece
export const StrawHat = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <Cyl p={[0, 0.006, 0]} a={[0.21, 0.21, 0.012, 40]} c="#E3B53C" r={0.9} t="fabric" tr={[6, 6]} />
    <Cyl p={[0, 0.05, 0]} a={[0.1, 0.115, 0.08, 32]} c="#E3B53C" r={0.9} t="fabric" tr={[6, 2]} />
    <Ball p={[0, 0.088, 0]} rad={0.1} c="#E3B53C" sc={[1, 0.25, 1]} t="fabric" tr={[6, 3]} />
    <Cyl p={[0, 0.03, 0]} a={[0.117, 0.118, 0.03, 32]} c="#C0262B" r={0.7} t="fabric" />
  </group>
);

// Figurine chibi de Gojo — Jujutsu Kaisen
export const Gojo = ({ position, rotation }) => {
  const spikes = useMemo(() => Array.from({ length: 16 }, (_, i) => {
    const a = (i / 16) * Math.PI * 2;
    const up = i % 2 ? 0.8 : 0.35;
    return { p: [Math.cos(a) * 0.045, 0.2 + up * 0.03, Math.sin(a) * 0.045], r: [Math.sin(a) * up, 0, -Math.cos(a) * up] };
  }), []);
  return (
    <group position={position} rotation={rotation}>
      <Cyl p={[0, 0.008, 0]} a={[0.05, 0.05, 0.016, 24]} c="#1A1A1A" r={0.3} />
      <mesh position={[0, 0.07, 0]} castShadow>
        <capsuleGeometry args={[0.035, 0.06, 4, 12]} />
        <Mat c="#1F2340" r={0.6} />
      </mesh>
      <Ball p={[0, 0.17, 0]} rad={0.055} c="#F0D2B8" r={0.5} />
      <Cyl p={[0, 0.172, 0]} a={[0.057, 0.057, 0.022, 24]} c="#111" r={0.6} />
      {spikes.map((s, i) => (
        <mesh key={i} position={s.p} rotation={s.r} castShadow>
          <coneGeometry args={[0.022, 0.07, 6]} />
          <Mat c="#F4F6FA" r={0.4} />
        </mesh>
      ))}
      <Ball p={[0, 0.215, 0]} rad={0.05} c="#F4F6FA" r={0.4} sc={[1, 0.6, 1]} />
    </group>
  );
};

// Bâton de Frieren
export const FrierenStaff = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <Cyl p={[0, 0.75, 0]} a={[0.016, 0.02, 1.5, 12]} c="#E7E2D6" r={0.4} t="wood" />
    <Cyl p={[0, 1.38, 0]} a={[0.024, 0.024, 0.06, 12]} c="#C9A54A" m={0.8} r={0.3} />
    <mesh position={[0, 1.55, 0]} rotation={[0, 0, Math.PI * 0.2]}>
      <torusGeometry args={[0.1, 0.012, 10, 32, Math.PI * 1.55]} />
      <Mat c="#C9A54A" m={0.85} r={0.25} />
    </mesh>
    <mesh position={[0, 1.55, 0]}>
      <octahedronGeometry args={[0.04, 0]} />
      <meshStandardMaterial color="#C2262E" roughness={0.1} metalness={0.3} emissive="#6A0F14" />
    </mesh>
  </group>
);

// Mimic — le coffre piégé de Frieren
export const Mimic = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <Box p={[0, 0.12, 0]} s={[0.5, 0.24, 0.34]} c="#6E4428" t="wood" round={0.015} />
    {[-0.17, 0.17].map((x) => <Box key={x} p={[x, 0.12, 0]} s={[0.04, 0.245, 0.345]} c="#C9A54A" m={0.7} r={0.35} />)}
    <Box p={[0, 0.17, 0.0]} s={[0.46, 0.06, 0.3]} c="#3A0E10" r={0.9} shadow={false} />
    <group position={[0, 0.24, -0.17]} rotation={[-0.55, 0, 0]}>
      <Box p={[0, 0.05, 0.17]} s={[0.5, 0.1, 0.34]} c="#6E4428" t="wood" round={0.02} />
      {Array.from({ length: 9 }, (_, i) => (
        <mesh key={i} position={[-0.2 + i * 0.05, -0.01, 0.32]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.016, 0.05, 6]} />
          <Mat c="#F2EBDD" r={0.4} />
        </mesh>
      ))}
    </group>
    {Array.from({ length: 9 }, (_, i) => (
      <mesh key={`b${i}`} position={[-0.2 + i * 0.05, 0.255, 0.155]}>
        <coneGeometry args={[0.016, 0.045, 6]} />
        <Mat c="#F2EBDD" r={0.4} />
      </mesh>
    ))}
    <Ball p={[0, 0.215, 0.05]} rad={0.06} c="#B83A4A" r={0.4} sc={[1.4, 0.3, 1.2]} />
    <Ball p={[-0.08, 0.27, 0.02]} rad={0.014} c="#F2C94C" e="#F2C94C" />
    <Ball p={[0.08, 0.27, 0.02]} rad={0.014} c="#F2C94C" e="#F2C94C" />
  </group>
);

// Cassettes audio de Hirayama — Perfect Days
export const Cassettes = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    {["#E8D6BB", "#C85A32", "#3E5C76"].map((c, i) => (
      <group key={c} position={[0, 0.011 + i * 0.022, 0]} rotation={[0, i * 0.12 - 0.1, 0]}>
        <Box s={[0.11, 0.02, 0.07]} c="#2B2A2E" r={0.3} t="plastic" round={0.003} />
        <Box p={[0, 0.0105, 0]} s={[0.085, 0.002, 0.045]} c={c} r={0.8} t="paper" shadow={false} />
      </group>
    ))}
    <group position={[0.08, 0.035, 0.0]} rotation={[0, -0.4, Math.PI / 2 - 0.15]}>
      <Box s={[0.07, 0.02, 0.11]} c="#2B2A2E" r={0.3} round={0.003} />
    </group>
  </group>
);

// Derrick miniature — There Will Be Blood
export const OilDerrick = ({ position, rotation }) => {
  const parts = useMemo(() => {
    const H = 1.05; const B = 0.2; const T = 0.05;
    const corners = [[1, 1], [1, -1], [-1, -1], [-1, 1]];
    const legs = corners.map(([sx, sz]) => [new THREE.Vector3(sx * B, 0.08, sz * B), new THREE.Vector3(sx * T, H, sz * T)]);
    const braces = [];
    for (let k = 0; k < 5; k += 1) {
      const y1 = 0.08 + (k / 5) * (H - 0.08);
      const y2 = 0.08 + ((k + 1) / 5) * (H - 0.08);
      const w1 = B + (T - B) * (k / 5);
      const w2 = B + (T - B) * ((k + 1) / 5);
      for (let c = 0; c < 4; c += 1) {
        const [ax, az] = corners[c];
        const [bx, bz] = corners[(c + 1) % 4];
        braces.push([new THREE.Vector3(ax * w1, y1, az * w1), new THREE.Vector3(bx * w2, y2, bz * w2)]);
        braces.push([new THREE.Vector3(ax * w2, y2, az * w2), new THREE.Vector3(bx * w2, y2, bz * w2)]);
      }
    }
    return [...legs.map((l) => [...l, 0.012]), ...braces.map((b) => [...b, 0.005])].map(([a, b, r]) => new THREE.TubeGeometry(new THREE.LineCurve3(a, b), 1, r, 5, false));
  }, []);
  return (
    <group position={position} rotation={rotation}>
      <Box p={[0, 0.04, 0]} s={[0.5, 0.08, 0.5]} c="#3D2415" t="wood" round={0.01} />
      <Cyl p={[0.14, 0.081, 0.14]} a={[0.07, 0.07, 0.004, 20]} c="#050505" r={0.05} m={0.3} shadow={false} />
      {parts.map((g, i) => (
        <mesh key={i} geometry={g} castShadow>
          <meshStandardMaterial color="#6B4A2E" roughness={0.8} />
        </mesh>
      ))}
      <Box p={[0, 1.07, 0]} s={[0.14, 0.04, 0.14]} c="#5C3A21" t="wood" />
    </group>
  );
};

// Ours sculpté — The Revenant
export const BearCarving = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <Ball p={[0, 0.07, 0]} rad={0.07} c="#6B4428" r={0.7} sc={[1.5, 0.95, 0.9]} t="wood" />
    <Ball p={[0.1, 0.1, 0]} rad={0.045} c="#6B4428" r={0.7} t="wood" />
    <Ball p={[0.145, 0.09, 0]} rad={0.022} c="#5A3820" r={0.7} sc={[1.3, 0.9, 0.9]} t="wood" />
    <Ball p={[0.09, 0.14, 0.03]} rad={0.013} c="#5A3820" />
    <Ball p={[0.09, 0.14, -0.03]} rad={0.013} c="#5A3820" />
    <Ball p={[-0.04, 0.12, 0]} rad={0.05} c="#6B4428" r={0.7} sc={[1, 0.7, 0.9]} t="wood" />
    {[[0.06, 0.04], [0.06, -0.04], [-0.06, 0.04], [-0.06, -0.04]].map(([x, z]) => (
      <Cyl key={`${x}${z}`} p={[x, 0.025, z]} a={[0.018, 0.02, 0.05, 10]} c="#5A3820" t="wood" />
    ))}
  </group>
);
